import { Link, type To } from "react-router-dom";
import { useTranslation } from "react-i18next";

export interface BreadcrumbItem {
  label: string;
  to?: To;
  state?: unknown;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  includeHome?: boolean;
  homeLabel?: string;
  className?: string;
}

export function Breadcrumbs({
  items,
  includeHome = true,
  homeLabel,
  className,
}: BreadcrumbsProps) {
  const { t } = useTranslation();
  const resolvedItems: BreadcrumbItem[] = includeHome
    ? [{ label: homeLabel ?? t("navigation.home"), to: "/dashboard" }, ...items]
    : items;

  const baseClasses =
    "text-sm text-slate-500 dark:text-slate-400";
  const listClasses =
    "flex flex-wrap items-center gap-2";
  const itemClasses = "flex items-center gap-2";
  const linkClasses =
    "text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200";
  const lastLabelClasses = "font-semibold text-slate-800 dark:text-slate-100";
  const navClasses = [baseClasses, className].filter(Boolean).join(" ");

  return (
    <nav className={navClasses} aria-label={t("navigation.breadcrumb_label")}>
      <ol className={listClasses}>
        {resolvedItems.map((item, index) => {
          const isLast = index === resolvedItems.length - 1;
          const key = `${item.label}-${index}`;

          const separator =
            index > 0 ? (
              <span
                aria-hidden="true"
                className="text-sm text-slate-400 dark:text-slate-600"
              >
                /
              </span>
            ) : null;

          if (isLast) {
            return (
              <li key={key} aria-current="page" className={itemClasses}>
                {separator}
                <strong className={lastLabelClasses}>{item.label}</strong>
              </li>
            );
          }

          if (item.to) {
            return (
              <li key={key} className={itemClasses}>
                {separator}
                <Link to={item.to} state={item.state} className={linkClasses}>
                  {item.label}
                </Link>
              </li>
            );
          }

          return (
            <li key={key} className={itemClasses}>
              {separator}
              <span className="text-slate-500 dark:text-slate-400">{item.label}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
